import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { CLP, FREQUENCIES } from "@/lib/api";
import { PageContainer, StatusBadge, PrimaryButton, SecondaryButton, EmptyState } from "@/components/ui-kit";
import {
  MapPin, Phone, Mail, ArrowLeft, Plus, FileText, Calendar, Hash,
  FileSignature, UserX, UserCheck, Pencil, AlertTriangle, Receipt,
  DollarSign, Folder, ClipboardList, Users, Wrench, Bell,
} from "lucide-react";
import ServiceForm from "@/components/ServiceForm";
import CustomerForm, { CLASSIFICATIONS } from "@/components/CustomerForm";
import ContractModal from "@/components/ContractModal";
import CustomerDriveFiles from "@/components/CustomerDriveFiles";
import CustomerRepairHistory from "@/components/CustomerRepairHistory";
import { CustomerVisitTimeline } from "@/components/CustomerVisitTimeline";
import InvoiceForm from "@/components/InvoiceForm";
import QuoteForm from "@/components/QuoteForm";
import FinanceEntryForm from "@/components/FinanceEntryForm";
import { toast } from "sonner";

const freqLabel = (v) => FREQUENCIES.find((f) => f.value === v)?.label || v;
const CLASS_MAP = Object.fromEntries(CLASSIFICATIONS.map((c) => [c.value, c]));

const TABS = [
  { key: "resumen",       label: "Resumen",       icon: ClipboardList },
  { key: "contactos",     label: "Contactos",     icon: Users },
  { key: "servicios",     label: "Servicios",     icon: Calendar },
  { key: "informes",      label: "Informes y envíos", icon: Wrench },
  { key: "cotizaciones",  label: "Cotizaciones",  icon: FileText },
  { key: "finanzas",      label: "Finanzas",      icon: DollarSign },
  { key: "archivos",      label: "Archivos",      icon: Folder },
];

function ClassificationChip({ value }) {
  const meta = CLASS_MAP[value] || CLASS_MAP.activo_contrato;
  return (
    <span
      data-testid="customer-classification-chip"
      className="font-mono-kpi text-[9px] uppercase tracking-wider px-2 py-0.5 border"
      style={{ color: meta.color, borderColor: `${meta.color}55`, background: `${meta.color}0d` }}
    >
      {meta.label}
    </span>
  );
}

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [services, setServices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [tab, setTab] = useState("resumen");
  const [showService, setShowService] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showContract, setShowContract] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [showQuote, setShowQuote] = useState(false);
  const [showCost, setShowCost] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const loadAll = async () => {
    try {
      const [c, s, q, inv] = await Promise.all([
        api.get(`/customers/${id}`),
        api.get("/services", { params: { customer_id: id } }),
        api.get("/quotes", { params: { customer_id: id } }),
        api.get("/invoices", { params: { customer_id: id } }),
      ]);
      setCustomer(c.data);
      setServices(s.data);
      setQuotes(q.data);
      setInvoices(inv.data);
      setLoadError(null);
    } catch (e) {
      setLoadError(e.response?.data?.detail || e.message || "Error de conexión");
      toast.error("No se pudo cargar el expediente");
    }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id]);

  if (loadError && !customer) {
    return (
      <PageContainer>
        <Link to="/admin/clientes" className="inline-flex items-center gap-1.5 text-xs font-mono-kpi uppercase tracking-[0.2em] text-neutral-500 hover:text-[#FF5A00] mb-4">
          <ArrowLeft className="w-3 h-3" /> Volver a clientes
        </Link>
        <EmptyState title="No se pudo cargar el cliente" description={loadError} />
      </PageContainer>
    );
  }
  if (!customer) {
    return (
      <PageContainer>
        <div className="font-mono-kpi text-xs text-neutral-500 uppercase tracking-[0.2em]">Cargando…</div>
      </PageContainer>
    );
  }

  const toggleActive = async () => {
    const action = customer.active ? "deactivate" : "activate";
    const verb = customer.active ? "dar de baja" : "reactivar";
    if (!window.confirm(`¿${verb.charAt(0).toUpperCase() + verb.slice(1)} a ${customer.name}? Se conservará todo el historial.`)) return;
    try {
      const { data } = await api.post(`/customers/${id}/${action}`);
      setCustomer(data);
      toast.success(customer.active ? "Cliente dado de baja" : "Cliente reactivado");
    } catch (e) {
      toast.error("No se pudo actualizar el estado del cliente");
    }
  };

  const completedServices = services.filter((s) => s.status === "completed").length;
  const unpaidInvoices = invoices.filter((i) => i.status !== "pagado");
  const overdueInvoices = invoices.filter((i) => i.is_overdue);
  const hasDelinquentAlert = overdueInvoices.length >= 2;
  const totalDebt = unpaidInvoices.reduce((a, i) => a + (i.gross_amount || 0), 0);

  // Recuentos por tab (para badges)
  const tabCounts = {
    servicios: services.length,
    informes: services.filter((s) => s.status === "completed").length,
    cotizaciones: quotes.length,
    finanzas: invoices.length,
  };

  return (
    <PageContainer>
      <Link
        to="/admin/clientes"
        data-testid="customer-back"
        className="inline-flex items-center gap-1.5 text-xs font-mono-kpi uppercase tracking-[0.2em] text-neutral-500 hover:text-[#FF5A00] mb-4"
      >
        <ArrowLeft className="w-3 h-3" /> Volver a clientes
      </Link>

      {/* Header — cliente name + classification + operation stats */}
      <div className="bg-white border border-neutral-200 p-5 mb-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">
                {freqLabel(customer.frequency)}{customer.active ? "" : " · DADO DE BAJA"}
              </div>
              <ClassificationChip value={customer.classification || "activo_contrato"} />
              {customer.requires_oc && (
                <span className="font-mono-kpi text-[9px] uppercase tracking-wider bg-[#FFC107]/15 text-[#B28C00] border border-[#FFC107]/40 px-1.5 py-0.5 inline-flex items-center gap-0.5">
                  <Receipt className="w-2.5 h-2.5" />Con OC
                </span>
              )}
            </div>
            <h1 className="font-display font-bold text-3xl mt-1 leading-tight">{customer.name}</h1>
            <div className="mt-2 flex items-start gap-2 text-sm text-neutral-600">
              <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-neutral-400" />
              <span>{customer.address}{customer.commune ? `, ${customer.commune}` : ""}{customer.city ? `, ${customer.city}` : ""}</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:gap-3 w-full sm:w-auto sm:min-w-[280px]">
            <MiniStat label="Servicios" value={services.length} sub={`${completedServices} OK`} />
            <MiniStat label="Deuda" value={CLP(totalDebt)} sub={`${unpaidInvoices.length} impagas`} accent={totalDebt > 0 ? "text-[#B71C1C]" : ""} />
            <MiniStat label="Vencidas" value={overdueInvoices.length} accent={overdueInvoices.length > 0 ? "text-[#D50000]" : ""} />
          </div>
        </div>
      </div>

      {hasDelinquentAlert && (
        <div className="mb-4 border border-[#D50000]/40 bg-[#D50000]/5 p-3 flex items-center gap-3" data-testid="customer-delinquent-alert">
          <AlertTriangle className="w-4 h-4 text-[#D50000] flex-shrink-0" />
          <div className="text-sm">
            <b className="text-[#B71C1C]">Cliente moroso</b> — {overdueInvoices.length} facturas vencidas. Revisa antes de agendar.
          </div>
        </div>
      )}

      {/* Sticky action bar */}
      <div className="sticky top-0 z-30 bg-white border border-neutral-200 mb-4 shadow-sm" data-testid="customer-action-bar">
        <div className="flex items-center gap-2 flex-wrap px-4 py-2.5">
          {customer.active && (
            <PrimaryButton onClick={() => setShowService(true)} data-testid="customer-action-new-service">
              <Plus className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Nuevo servicio
            </PrimaryButton>
          )}
          <SecondaryButton onClick={() => setShowQuote(true)} data-testid="customer-action-new-quote">
            <FileText className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Nueva cotización
          </SecondaryButton>
          <SecondaryButton onClick={() => setShowInvoice(true)} data-testid="customer-action-new-invoice">
            <Receipt className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Nueva factura
          </SecondaryButton>
          <SecondaryButton onClick={() => setShowCost(true)} data-testid="customer-action-new-cost">
            <DollarSign className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Registrar costo
          </SecondaryButton>
          <div className="flex-1" />
          <SecondaryButton onClick={() => setShowContract(true)} data-testid="customer-action-contract">
            <FileSignature className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Contrato
          </SecondaryButton>
          <SecondaryButton onClick={() => setShowEdit(true)} data-testid="customer-action-edit">
            <Pencil className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Editar
          </SecondaryButton>
          <button
            data-testid="customer-action-toggle-active"
            onClick={toggleActive}
            className={`px-3 py-2 font-mono-kpi text-xs uppercase tracking-[0.15em] transition-colors ${
              customer.active
                ? "bg-white text-[#D50000] border border-[#D50000]/30 hover:bg-[#D50000]/5"
                : "bg-[#00C853] text-white hover:bg-[#008C3A]"
            }`}
          >
            {customer.active ? (
              <><UserX className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Dar de baja</>
            ) : (
              <><UserCheck className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Reactivar</>
            )}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-neutral-200 mb-6 flex gap-1 overflow-x-auto" data-testid="customer-tabs">
        {TABS.map((t) => {
          const isActive = tab === t.key;
          const count = tabCounts[t.key];
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`customer-tab-${t.key}`}
              className={`px-4 py-2.5 -mb-px text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
                isActive
                  ? "border-[#FF5A00] text-[#0A0A0A]"
                  : "border-transparent text-neutral-500 hover:text-[#0A0A0A]"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
              {count != null && count > 0 && (
                <span className={`font-mono-kpi text-[10px] px-1.5 py-0.5 ${isActive ? "bg-[#FF5A00] text-white" : "bg-neutral-100 text-neutral-500"}`}>{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab contents */}
      {tab === "resumen" && <ResumenTab customer={customer} services={services} quotes={quotes} invoices={invoices} />}
      {tab === "contactos" && <ContactosTab customer={customer} />}
      {tab === "servicios" && <ServiciosTab services={services} />}
      {tab === "informes" && <><CustomerVisitTimeline customerId={id} /><div className="mt-8"><CustomerRepairHistory customerId={id} /></div></>}
      {tab === "cotizaciones" && <CotizacionesTab quotes={quotes} />}
      {tab === "finanzas" && <FinanzasTab invoices={invoices} />}
      {tab === "archivos" && <CustomerDriveFiles customerId={id} />}

      {/* Modals */}
      {showService && (
        <ServiceForm
          defaultCustomerId={id}
          onClose={() => setShowService(false)}
          onSubmit={async (payload) => {
            await api.post("/services", payload);
            toast.success("Servicio programado");
            loadAll();
          }}
        />
      )}
      {showEdit && (
        <CustomerForm
          initial={customer}
          onClose={() => setShowEdit(false)}
          onSubmit={async (payload) => {
            await api.patch(`/customers/${id}`, payload);
            toast.success("Cliente actualizado");
            loadAll();
          }}
        />
      )}
      {showContract && (
        <ContractModal customerId={id} customerName={customer.name} onClose={() => setShowContract(false)} />
      )}
      {showInvoice && (
        <InvoiceForm
          defaultCustomerId={id}
          onClose={() => setShowInvoice(false)}
          onSaved={() => { setShowInvoice(false); loadAll(); }}
        />
      )}
      {showQuote && (
        <QuoteForm
          defaultCustomerId={id}
          onClose={() => setShowQuote(false)}
          onCreated={(q) => { setShowQuote(false); navigate(`/admin/cotizaciones/${q.id}`); }}
        />
      )}
      {showCost && (
        <FinanceEntryForm
          defaultCategory={customer.name}
          defaultDescription={`Costo asociado a ${customer.name}`}
          onClose={() => setShowCost(false)}
          onCreated={() => { setShowCost(false); toast.success("Costo registrado"); }}
        />
      )}
    </PageContainer>
  );
}

/* ─────────────────────────── Reusable pieces ─────────────────────────── */

function MiniStat({ label, value, sub, accent = "" }) {
  return (
    <div className="border border-neutral-200 bg-neutral-50 px-3 py-2">
      <div className="font-mono-kpi text-[9px] uppercase tracking-[0.15em] text-neutral-500">{label}</div>
      <div className={`font-mono-kpi font-bold text-base leading-tight mt-0.5 ${accent || "text-[#0A0A0A]"}`}>{value}</div>
      {sub && <div className="text-[10px] text-neutral-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <section className="bg-white border border-neutral-200 p-5">
      <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00] mb-3">// {title}</div>
      {children}
    </section>
  );
}

/* ─────────────────────────── Tabs ─────────────────────────── */

function ResumenTab({ customer, services, quotes, invoices }) {
  const nextService = services.find((s) => s.status === "scheduled");
  const recentQuotes = quotes.slice(0, 3);
  const recentInvoices = invoices.slice(0, 3);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <SectionCard title="Información contractual">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <div className="font-mono-kpi text-[9px] uppercase tracking-[0.15em] text-neutral-500">Precio mensual</div>
            <div className="font-mono-kpi font-bold text-[#FF5A00] text-lg">{(customer.price_uf || 0).toLocaleString("es-CL", { maximumFractionDigits: 2 })} UF</div>
          </div>
          <div>
            <div className="font-mono-kpi text-[9px] uppercase tracking-[0.15em] text-neutral-500">Día factura</div>
            <div className="font-bold text-base">{customer.billing_day || "—"}</div>
          </div>
          {customer.rut && (
            <div className="col-span-2">
              <div className="font-mono-kpi text-[9px] uppercase tracking-[0.15em] text-neutral-500">RUT</div>
              <div className="font-mono-kpi text-sm">{customer.rut}</div>
            </div>
          )}
          {nextService && (
            <div className="col-span-2 pt-2 border-t border-neutral-100">
              <div className="font-mono-kpi text-[9px] uppercase tracking-[0.15em] text-neutral-500">Próximo servicio</div>
              <div className="text-sm font-medium">{nextService.scheduled_date}</div>
            </div>
          )}
        </div>
      </SectionCard>

      {(customer.special_schedule || customer.special_considerations) && (
        <SectionCard title="Consideraciones operativas">
          {customer.special_schedule && (
            <div className="mb-3">
              <div className="font-mono-kpi text-[9px] uppercase tracking-[0.15em] text-neutral-500 mb-1">Horarios</div>
              <div className="text-sm whitespace-pre-wrap">{customer.special_schedule}</div>
            </div>
          )}
          {customer.special_considerations && (
            <div>
              <div className="font-mono-kpi text-[9px] uppercase tracking-[0.15em] text-neutral-500 mb-1">Avisos / accesos</div>
              <div className="text-sm whitespace-pre-wrap">{customer.special_considerations}</div>
            </div>
          )}
        </SectionCard>
      )}

      {customer.notes && (
        <SectionCard title="Notas internas">
          <div className="text-sm whitespace-pre-wrap text-neutral-700">{customer.notes}</div>
        </SectionCard>
      )}

      {recentQuotes.length > 0 && (
        <SectionCard title="Últimas cotizaciones">
          <div className="space-y-2">
            {recentQuotes.map((q) => (
              <Link key={q.id} to={`/admin/cotizaciones/${q.id}`} className="flex items-center justify-between text-sm hover:text-[#FF5A00]">
                <span className="truncate">{q.title}</span>
                <span className="font-mono-kpi font-bold">{CLP(q.total)}</span>
              </Link>
            ))}
          </div>
        </SectionCard>
      )}

      {recentInvoices.length > 0 && (
        <SectionCard title="Últimas facturas">
          <div className="space-y-2">
            {recentInvoices.map((i) => (
              <div key={i.id} className="flex items-center justify-between text-sm">
                <span className="truncate">{i.concept}</span>
                <StatusBadge status={i.is_overdue ? "vencido" : i.status} />
              </div>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
}

function ContactosTab({ customer }) {
  const contacts = customer.contacts || [];
  return (
    <SectionCard title={`Contactos (${contacts.length})`}>
      {contacts.length === 0 ? (
        <EmptyState title="Sin contactos" description="Agrega contactos editando el cliente desde la barra superior." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {contacts.map((c, i) => (
            <div key={`${c.email || c.name || i}`} data-testid={`customer-contact-card-${i}`} className="border border-neutral-200 p-3">
              <div className="flex items-center justify-between">
                <div className="font-bold text-sm">{c.name || "Sin nombre"}</div>
                {c.notify_before_visit && (
                  <span title="Recibe aviso 24h antes" className="inline-flex items-center gap-1 text-[10px] font-mono-kpi uppercase tracking-wider text-[#FF5A00]">
                    <Bell className="w-3 h-3" />Avisar
                  </span>
                )}
              </div>
              {c.role && <div className="text-xs text-neutral-500 mt-0.5">{c.role}</div>}
              {c.email && (
                <a href={`mailto:${c.email}`} className="text-xs text-neutral-700 hover:text-[#FF5A00] flex items-center gap-1 mt-2">
                  <Mail className="w-3 h-3" />{c.email}
                </a>
              )}
              {c.phone && (
                <a href={`https://wa.me/${c.phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="text-xs text-neutral-700 hover:text-[#25D366] flex items-center gap-1 mt-1">
                  <Phone className="w-3 h-3" />{c.phone}
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

function ServiciosTab({ services }) {
  if (services.length === 0) {
    return <EmptyState title="Sin servicios" description="Agenda uno desde la barra superior." />;
  }
  return (
    <div className="bg-white border border-neutral-200 divide-y divide-neutral-100" data-testid="customer-services-list">
      {services.map((s) => (
        <Link key={s.id} to={`/admin/servicios/${s.id}`} className="block px-5 py-3 hover:bg-neutral-50">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-mono-kpi font-bold text-sm">{s.scheduled_date}</div>
              <div className="text-xs text-neutral-500 truncate max-w-md">
                {s.observations || s.repairs_needed || "Sin observaciones"}
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              {s.technician && <div className="text-xs text-neutral-600">{s.technician}</div>}
              <StatusBadge status={s.status} />
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

function InformesTab({ customerId, services }) {
  const completed = services.filter((s) => s.status === "completed");
  return (
    <div className="space-y-4">
      {completed.length === 0 ? (
        <EmptyState title="Sin informes disponibles" description="Los informes se generan a partir de servicios completados." />
      ) : (
        <div className="bg-white border border-neutral-200 divide-y divide-neutral-100" data-testid="customer-informes-list">
          {completed.map((s) => (
            <div key={s.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`customer-informe-${s.id}`}>
              <div className="min-w-0">
                <div className="font-mono-kpi font-bold text-sm">{s.scheduled_date}</div>
                <div className="text-xs text-neutral-500 truncate max-w-md">{s.observations || s.repairs_needed || "Sin observaciones"}</div>
              </div>
              <Link to={`/admin/servicios/${s.id}`} className="font-mono-kpi text-[10px] uppercase tracking-[0.15em] text-[#FF5A00] hover:underline">
                Ver informe →
              </Link>
            </div>
          ))}
        </div>
      )}
      <CustomerRepairHistory customerId={customerId} />
    </div>
  );
}

function CotizacionesTab({ quotes }) {
  if (quotes.length === 0) {
    return <EmptyState title="Sin cotizaciones" description="Crea una desde la barra superior." />;
  }
  return (
    <div className="bg-white border border-neutral-200 divide-y divide-neutral-100" data-testid="customer-quotes-list">
      {quotes.map((q) => (
        <Link key={q.id} to={`/admin/cotizaciones/${q.id}`} className="block px-5 py-3 hover:bg-neutral-50">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-bold text-sm truncate">{q.title}</div>
              <div className="text-xs text-neutral-500 font-mono-kpi">{q.number}</div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className="font-mono-kpi font-bold text-sm">{CLP(q.total)}</span>
              <StatusBadge status={q.status} />
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

function FinanzasTab({ invoices }) {
  const totalDebt = invoices.filter((i) => i.status !== "pagado").reduce((a, i) => a + (i.gross_amount || 0), 0);
  const totalPaid = invoices.filter((i) => i.status === "pagado").reduce((a, i) => a + (i.gross_amount || 0), 0);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <MiniStat label="Total facturado" value={CLP(totalDebt + totalPaid)} />
        <MiniStat label="Pagado" value={CLP(totalPaid)} accent="text-[#00C853]" />
        <MiniStat label="Por cobrar" value={CLP(totalDebt)} accent={totalDebt > 0 ? "text-[#B71C1C]" : ""} />
      </div>
      {invoices.length === 0 ? (
        <EmptyState title="Sin facturas" description="Crea una desde la barra superior." />
      ) : (
        <div className="bg-white border border-neutral-200 divide-y divide-neutral-100" data-testid="customer-invoices-list">
          <div className="flex items-center justify-between px-5 py-3 bg-neutral-50 border-b border-neutral-200">
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.15em] text-neutral-500">Historial completo</div>
            <Link to="/admin/finanzas" className="font-mono-kpi text-[10px] uppercase tracking-[0.15em] text-[#FF5A00] hover:underline">
              Ver Finanzas →
            </Link>
          </div>
          {invoices.map((i) => (
            <div key={i.id} className="px-5 py-3" data-testid={`customer-invoice-${i.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-bold text-sm truncate">{i.concept}</div>
                  <div className="text-xs text-neutral-500 font-mono-kpi">
                    {i.invoice_number ? `N° ${i.invoice_number}` : "sin número"}
                    {i.issue_date && ` · Emisión ${i.issue_date}`}
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="text-right">
                    <div className="font-mono-kpi font-bold text-sm">{CLP(i.gross_amount)}</div>
                    <div className="text-[10px] text-neutral-400 font-mono-kpi">Neto {CLP(i.net_amount)}</div>
                  </div>
                  <StatusBadge status={i.is_overdue ? "vencido" : i.status} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
